/**
 * Interactive UI guides — named, code-reviewed walkthroughs that Scout can
 * offer ("Do you want me to show you?") and the tour engine plays.
 *
 * Shared between server and UI on purpose: the server (Scout's
 * offer_ui_guide tool) validates guide ids + role gates so the model can
 * only ever offer walkthroughs that exist and that the user may perform;
 * the UI resolves the same id to steps and plays them. Because guides are
 * code, they change in the same PR as the screens they walk through —
 * a ui test asserts every anchor referenced here exists in the source.
 *
 * Step routes may contain ":tenantId", substituted by the tour engine.
 * Steps without an anchor render as a centered card (no spotlight).
 */
export const GUIDE_ROLE_LEVEL = {
    standard_user: 0,
    agent_admin: 1,
    tenant_admin: 2,
};
export const UI_GUIDES = [
    {
        id: "platform-basics",
        title: "Platform basics",
        requiredRole: "standard_user",
        summary: "The first-login tour: agents, channels, chats, composer, files, Scout.",
        steps: [
            {
                route: "/tenants/:tenantId/workspace",
                anchor: "[data-tour='agent-picker']",
                title: "Your agents",
                body: "Pick which AI agent you're talking to here. Clicking the name opens the agent's General channel; the arrow opens a searchable list of every agent you have access to.",
            },
            {
                anchor: "[data-tour='channels']",
                title: "Channels",
                body: "Channels are shared spaces for a topic or team — everyone in the channel sees the conversation. Chats outside a channel stay private to you.",
            },
            {
                anchor: "[data-tour='chats']",
                title: "Recent chats",
                body: "Your conversations with the selected agent. Each chat keeps its own context — use New chat when you switch topics.",
            },
            {
                anchor: "[data-tour='composer']",
                title: "Talk to your agent",
                body: "Type here and press Enter to send. Type @ to bring another agent into the conversation or to reference a workspace file, and / to run a skill.",
            },
            {
                anchor: "[data-tour='attach']",
                title: "Share files",
                body: "The + button attaches files — documents and images (up to 5 per message) that your agent can read and work with.",
            },
            {
                anchor: "[data-tour='scout']",
                title: "Meet Scout",
                body: "Stuck, or something isn't behaving? Scout explains how the platform works, checks agent health and workflow runs, and can raise a support request to the platform team.",
            },
        ],
    },
    {
        id: "add-mcp-server",
        title: "Add an MCP server to an agent",
        requiredRole: "tenant_admin",
        summary: "Enable or create an MCP server for the workspace, then assign it to an agent.",
        steps: [
            {
                route: "/tenants/:tenantId/settings/mcp",
                anchor: "[data-tour='mcp-list']",
                title: "Your MCP servers",
                body: "This is your workspace's MCP server catalogue. Servers your platform provider has published appear here — click Enable on one to switch it on for your workspace.",
            },
            {
                anchor: "[data-tour='mcp-create']",
                title: "Or register your own",
                body: "Create MCP registers your own server: name, transport (HTTP, SSE or Stdio), URL and authentication. Secrets you enter are stored in your company's private vault. Saving also enables it.",
            },
            {
                route: "/tenants/:tenantId/agents",
                anchor: "[data-tour='agents-list']",
                title: "Now give it to an agent",
                body: "Open the agent that should get the tools, then go to its Config tab.",
            },
            {
                title: "Assign and sync",
                body: "On the Config tab, find the \"MCP Servers\" card. Use \"Assign MCP from tenant pool…\" and click Assign — syncing to the agent happens automatically. Watch the badge turn green (synced); expand the row to restrict which tools this agent gets.",
            },
        ],
    },
    {
        id: "create-workflow",
        title: "Build a workflow",
        requiredRole: "agent_admin",
        summary: "Where workflows live and the pieces they're made of.",
        steps: [
            {
                route: "/tenants/:tenantId/workspace",
                anchor: "[data-tour='mode-workflows']",
                title: "Workflows live here",
                body: "The Workflows tab shows your automations and opens the visual canvas where you build them.",
            },
            {
                title: "The building blocks",
                body: "Every workflow has exactly one trigger (manual, schedule, channel message or webhook), then agent steps, yes/no or multiple-choice conditions, and actions joined by edges. Test with a manual run before attaching a schedule.",
            },
            {
                anchor: "[data-tour='composer']",
                title: "Or just ask",
                body: "You can also describe the workflow to an agent right here in chat — it will draft the workflow for you, and you refine it on the canvas.",
            },
        ],
    },
    {
        id: "raise-support-ticket",
        title: "Raise a support request",
        requiredRole: "standard_user",
        summary: "Get a problem to the human platform team and follow its progress.",
        steps: [
            {
                anchor: "[data-tour='scout']",
                title: "Start with Scout",
                body: "Describe the problem to Scout first — it can often diagnose agents and workflows on the spot. If it can't fix it, it will offer to raise a support request for you.",
            },
            {
                title: "Follow your request",
                body: "Your requests live in Scout's Support view (the life-buoy icon in its header). You'll be notified in the platform — and on Teams or email if you chose that — whenever the platform team replies or the status changes.",
            },
        ],
    },
];
export function getUiGuide(id) {
    return UI_GUIDES.find((g) => g.id === id);
}
//# sourceMappingURL=ui-guides.js.map